import { axiosIns } from "../utils";

const toNumber = (value) => {
  const next = Number(value);
  return Number.isNaN(next) ? 0 : next;
};

/**
 * Normalises the reputation figures the API now returns alongside each expert.
 *
 * `satisfaction_rate` is deliberately left as `null` when the API reports it
 * so: an expert nobody has judged yet is unrated, and coercing that to 0 would
 * display them as terrible rather than as new.
 */
const withStats = (expert) => ({
  ...expert,
  questions_handled: toNumber(expert?.questions_handled),
  questions_satisfied: toNumber(expert?.questions_satisfied),
  satisfaction_rate:
    expert?.satisfaction_rate === null || expert?.satisfaction_rate === undefined
      ? null
      : toNumber(expert.satisfaction_rate),
});

export const fetchExperts = async () => {
  const response = await axiosIns.get("/expert/");
  const data = response && response.data ? response.data : response;
  const rows = Array.isArray(data) ? data : data?.results;
  return Array.isArray(rows) ? rows.map(withStats) : data;
};

/**
 * An application from the mobile app that nobody has decided on yet: the
 * professional asked to become an expert and is not one. Lives here rather
 * than in the hook so the service can filter with it without importing the
 * hook back (cycle); useExperts re-exports it for the pages.
 */
export const isPendingExpertRequest = (expert) =>
  Boolean(expert?.expert_request) && !expert?.is_expert;

/**
 * Pending applications, newest first as the API orders them.
 *
 * `?requests=1` is admin/committee only (403 otherwise). The filter is applied
 * client-side as well: a backend deployed before the parameter existed ignores
 * it and returns every professional, and the badge must not count them.
 */
export const fetchPendingExpertRequests = async () => {
  const response = await axiosIns.get("/expert/?requests=1");
  const data = response && response.data ? response.data : response;
  const rows = Array.isArray(data) ? data : data?.results;
  return Array.isArray(rows)
    ? rows.map(withStats).filter(isPendingExpertRequest)
    : [];
};

export const fetchExpert = async (id) => {
  const response = await axiosIns.get(`/expert/${id}/`);
  const data = response && response.data ? response.data : response;
  return data ? withStats(data) : data;
};

export const updateExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/`, payload);
  return response && response.data ? response.data : response;
};

export const validateExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/validate/`, payload);
  return response && response.data ? response.data : response;
};

export const unvalidateExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/unvalidate/`, payload);
  return response && response.data ? response.data : response;
};

export const approveExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/approve/`, payload);
  return response && response.data ? response.data : response;
};

export const rejectExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/reject/`, payload);
  return response && response.data ? response.data : response;
};

export const recommendExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(`/expert/${id}/recommand/`, payload);
  return response && response.data ? response.data : response;
};

export const certifyExpertApi = async (id, payload) => {
  const response = await axiosIns.patch(
    `/professional/${id}/certify/`,
    payload
  );
  return response && response.data ? response.data : response;
};
